import React, { useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Button } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import TimeZones from "@app/enum/TimeZones";
import { getOrganizationSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import TimeZone from "@app/types/TimeZone";
import { getLocalTimeZone, getOffset } from "@app/util/dates";

interface Props {
  className?: string;
  organizationTimezone?: string;
}

const TimeOffsetAlert: React.FC<Props> = ({ className, organizationTimezone }) => {
  const intl = useIntl();

  const [dismissed, setDismissed] = useBoolean(localSettings.getTimeOffsetAlertDismissed());

  const displayTimeZoneName = intl.formatMessage(TimeZones[organizationTimezone as TimeZone]);

  useEffect(() => {
    if (dismissed === localSettings.getTimeOffsetAlertDismissed()) return;
    localSettings.setTimeOffsetAlertDismissed(dismissed);
  }, [dismissed]);

  const localTimeZoneName = getLocalTimeZone();
  const localTimeZoneOffset = getOffset(localTimeZoneName);
  const orgTimeZoneOffset = getOffset(organizationTimezone ?? "UTC");

  const shouldDisplayTimeOffsetAlert = orgTimeZoneOffset !== localTimeZoneOffset;

  if (!shouldDisplayTimeOffsetAlert || dismissed) {
    return null;
  }

  return (
    <Alert
      action={[
        <Button key="dismiss" onClick={setDismissed} size="small" type="link">
          {intl.formatMessage({
            id: "reports.overview.timeOffset.alertDismissLink",
            defaultMessage: "Dismiss",
            description: "Timezone offset Alert link text on Overview Screen.",
          })}
        </Button>,
      ]}
      className={className}
      message={intl.formatMessage(
        {
          id: "reports.overview.timezone.alertMessage",
          defaultMessage:
            "Your local time zone does not match your organization’s selected time zone. Please note that date ranges on reports are shown according to your organization’s time zone: <tag>{displayTimeZoneName}</tag>.",
          description: "Timezone Alert message on Overview Screen.",
        },
        {
          displayTimeZoneName,
          tag: (text: string) => <strong>{text}</strong>,
        }
      )}
      showIcon
      type="error"
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  organizationTimezone: getOrganizationSettingValue(state)(OrganizationSetting.TIMEZONE),
});

export default connect(mapStateToProps)(TimeOffsetAlert);
